import moment from "moment";
import Teachr from "../../../Objects/models/Teachr";
import { PunctualCourseDashboardUpdate } from "../../../Objects/models/PunctualCourseDashboardUpdate";
import CourseProposalDashboard from "../../../Objects/models/CourseProposalDashbordUpdate";
import PunctualCourseRepository from "../../../Repositories/PunctualCourseRepository";
import CourseProposalRepository from "../../../Repositories/CourseProposalRepository";
import { capitalizeFirstLetter } from "../../../Utils/Function";
import { getActivePrestationBrandConfig } from "../../../config/environment.config";
import { normalizeSessionDescriptionOverride } from "../../../Utils/sessionDescriptionOverride";
import { sessionsToApiPayload } from "../../DeclarationCourse/SessionList";
import { describeOverlapApiError } from "../../DeclarationCourse/overlapMessages";
import { draftHours, draftPeriod } from "../../BrouillonsV2/drafts";
import { EditableCourse } from "../types";
import { needsDepositAttestation } from "../courseEditRules";

/**
 * Enregistrement d'un cours modifié : copie de `handleSubmitEdit` de
 * `CourseRowActions.tsx`, sans effets Redux.
 * 1. le cours (dates, durée, séances, déroulement, chapitre « over ») via
 *    `updatePunctualCourseByTeachr` ;
 * 2. la proposition acceptée (prix, acompte, attestation, preuves) via
 *    `updateCourseProposalCourseByTeachr`.
 */
export const submitCourseEdit = async (course: EditableCourse, teachr: Teachr, genericDescription: string | null, options: { over: boolean }): Promise<void> => {
    const { start, end } = draftPeriod(course);
    if (!start || !end) throw new Error("Période du cours incomplète");
    const hours = draftHours(course);
    const fees = parseFloat((teachr as any)?.defaultFees ?? "0") || 0;
    const price = parseFloat(course.price) || 0;

    const punctualCourse = new PunctualCourseDashboardUpdate(start.toDate(), end.toDate(), (hours * 2).toString());
    punctualCourse.chapter = options.over ? "over" : "";
    // le backend remplace les séances et re-dérive start/end/duration
    punctualCourse.sessions = course.multiple ? sessionsToApiPayload(course.sessions) : null;
    // déroulement propre à ce cours (null = retour à la description habituelle)
    punctualCourse.sessionDescriptionOverride = normalizeSessionDescriptionOverride(course.sessionDescriptionOverride, genericDescription);

    // relation Teach'r : le tarif fixé est envoyé tel quel (l'API le reprend de custom_fees)
    const proposals = new CourseProposalDashboard((course.isRelationTeachr ? price : price * 2 * (1 - fees)).toString(), start.toDate(), end.toDate());
    const depositAmount = course.deposit.enabled ? parseFloat(course.deposit.amount) || 0 : 0;
    const existing = course.existingDeposit;
    if (depositAmount > 0) {
        proposals.deposit = depositAmount;
        if (course.deposit.date) proposals.depositDate = moment(course.deposit.date).format("YYYY-MM-DD");
        // attestation sur l'honneur : exigée par le serveur si le montant ou la date change, ou si le cours n'en a pas encore
        proposals.depositAttestationAccepted = course.deposit.attestationAccepted === true;
        // un justificatif déposé est envoyé dès que l'attestation est (re)signée, même à acompte inchangé
        const sendProof = depositAmount !== existing.amount || needsDepositAttestation(course);
        const newProofId: number | null = course.deposit.proof?.id ?? null;
        if (course.multiple) {
            if (newProofId && sendProof) {
                // acompte inchangé : les justificatifs déjà enregistrés sont conservés
                const existingIds = depositAmount === existing.amount ? existing.proofIds : [];
                proposals.proofDeposits = [...existingIds, newProofId];
            }
        } else if (newProofId && existing.proofId !== newProofId && sendProof) {
            proposals.proofDeposit = newProofId;
        }
    } else if (existing.amount > 0 && depositAmount <= 0) {
        proposals.deposit = null;
        proposals.depositDate = null;
        proposals.proofDeposit = null;
        proposals.proofDeposits = null;
    }

    const updated = await PunctualCourseRepository.updatePunctualCourseByTeachr(course.id, punctualCourse);
    if (updated && course.proposalId) {
        await CourseProposalRepository.updateCourseProposalCourseByTeachr(course.proposalId, proposals);
    }
};

/** Message d'un refus du serveur, copie du dictionnaire de `handleSubmitEdit`. */
export const describeCourseEditError = (err: any): string => {
    const code = typeof err?.description === "string" ? err.description : "";
    const ce = getActivePrestationBrandConfig("", "ce ", "cette ");
    const e = getActivePrestationBrandConfig("", "", "e");
    const messages: Record<string, string> = {
        deposit_locked_urssaf: `L'acompte de ${ce} a déjà été transmis à l'URSSAF et ne peut plus être modifié.`,
        deposit_attestation_required: "Vous devez certifier sur l'honneur l'attestation d'acompte avant d'enregistrer.",
        proof_deposit_required: "Vous devez joindre une preuve d'acompte (PDF, PNG ou JPEG) avant d'enregistrer.",
        deposit_attestation_failed: "La génération de l'attestation d'acompte a échoué. La modification n'a pas été enregistrée, veuillez réessayer.",
        course_session_past_locked: "Une séance déjà passée ne peut plus être modifiée ni supprimée.",
        course_transmitted_to_urssaf: `${capitalizeFirstLetter(ce)} a déjà été transmis${e} à l'URSSAF et ne peut plus être modifié${e}.`,
        course_over_locked: `${capitalizeFirstLetter(ce)} est terminé${e} et ne peut plus être modifié${e}.`,
    };
    return messages[code]
        ?? describeOverlapApiError(code)
        ?? `La modification ${getActivePrestationBrandConfig("", "du ", "de la ")} a échoué. Vérifiez votre saisie puis réessayez.`;
};

export default submitCourseEdit;
